import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TapestryNotificationService } from '@predictiveindex/tapestry';
// NOTE: With TypeScript moduleResolution "bundler" deep paths that are not declared in the
// package "exports" field are intentionally blocked. The tapestry package only exports
// its public API root, so importing from
// '@predictiveindex/tapestry/lib/components/tapestry-notifications/models/tapestry-notification'
// fails under Angular 20. Instead of loosening module resolution or duplicating the interface,
// derive the notification type from the service API. This keeps us resilient to upstream changes.
type TapestryNotification = Parameters<
    TapestryNotificationService['create']
>[0];
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { appInjector } from '../app-injector';
import { CoreErrorNumber, ErrorNumber } from '../constants-types';
import { autoDismissNotificationTime } from '../constants-types/constants';
import { validatePromise } from '../core-utilities/decorator-util';
import { PIVersion } from '../shared';
import { InitDataService } from '../system-services';
import {
    ErrorNotificationMessage,
    MessagingService
} from '../system-services/Messaging.service';
import { TranslationService } from '../translations';
import { CustomNotificationService } from './CustomNotifications.service';

export function notification(
    notificationSuccessTitle: string,
    notificationSuccessMessage?: string,
    notificationErrorTitle?: string,
    notificationErrorMessage?: string
) {
    return function (_target: any, _name: string, property: any) {
        const fn = property.value;
        property.value = function (...args: any[]) {
            const notificationService = appInjector().get(NotificationService);
            const TR = appInjector().get(TranslationService);
            const customNotification = appInjector().get(
                CustomNotificationService
            );

            const promise = validatePromise(fn, this, args);
            return promise
                .then((passThrough) => {
                    if (notificationSuccessTitle) {
                        notificationService.pushSuccess(
                            TR.key(notificationSuccessTitle),
                            notificationSuccessMessage
                                ? TR.key(notificationSuccessMessage)
                                : ''
                        );
                    }
                    return passThrough;
                })
                .catch((e: Error) => {
                    // For core errors
                    if (
                        e instanceof HttpErrorResponse &&
                        e.error &&
                        e.error.Content?.Number &&
                        (!!errorToString[e.error.Content.Number] ||
                            !(
                                (e.url.includes('v1') ||
                                    e.url.includes('v2') ||
                                    e.url.includes('v3') ||
                                    (e.url.includes('v4') &&
                                        e.url.includes('org'))) &&
                                !!coreErrorToString[e.error.Content.Number]
                            ))
                    ) {
                        const errKeys =
                            !(
                                e.url.includes('v1') ||
                                e.url.includes('v2') ||
                                e.url.includes('v3') ||
                                (e.url.includes('v4') && e.url.includes('org'))
                            ) && !!coreErrorToString[e.error.Content.Number]
                                ? coreErrorToString[e.error.Content.Number]
                                : errorToString[e.error.Content.Number];
                        notificationService.pushError(
                            TR.key(notificationErrorTitle || errKeys.title),
                            TR.key(notificationErrorMessage || errKeys.message)
                        );
                    }
                    // For classic errors
                    else if (
                        e instanceof HttpErrorResponse &&
                        e.error &&
                        e.error.Number &&
                        errorToString[e.error.Number]
                    ) {
                        const errKeys = errorToString[e.error.Number];
                        notificationService.pushError(
                            TR.key(notificationErrorTitle || errKeys.title),
                            TR.key(notificationErrorMessage || errKeys.message)
                        );
                    }
                    // For classic synchronous batch errors
                    else if (e[0]?.error && e[0]?.error.error) {
                        e[0].error.error.forEach((error) => {
                            customNotification.pushCustomError(
                                error.errorResponse.ParameterErrors[0].Number,
                                error.errorResponse.ParameterErrors[0]
                                    .PropertyName,
                                notificationErrorTitle,
                                notificationErrorMessage
                            );
                        });
                    } else {
                        const errorTitle =
                            notificationErrorTitle ||
                            'notification.error-genericTitle';
                        const errorMessage =
                            notificationErrorMessage ||
                            'notification.error-genericMessage';
                        notificationService.pushError(
                            TR.key(errorTitle),
                            TR.key(errorMessage)
                        );
                    }
                    // Rethrow so the caller knows about the error occurring
                    throw e;
                });
        };
    };
}

const errorToString: {
    [key: number]: {
        title: string;
        message: string;
    };
} = {
    // taken from Enums.ts
    [ErrorNumber.RequiredCriteriaInvalid]: {
        // Error 1
        title: 'notification.error-cantProcessRequest',
        message: 'parametersAreNotValid'
    },
    [ErrorNumber.RequiredParameterMissing]: {
        // Error 2
        title: 'notification.error-cantProcessRequest',
        message: 'error.parametersAreMissing'
    },
    [ErrorNumber.ParameterMustBeLessThanParameterX]: {
        // Error 4
        title: 'notification.error-cantProcessRequest',
        message: 'error.parameterMustBeLessThanParameterX'
    },
    [ErrorNumber.EndpointUnreachable]: {
        // Error 5
        title: 'notification.error-cantProcessRequest',
        message: 'error.endpointUnreachable'
    },
    [ErrorNumber.InternalError]: {
        // Error 6
        title: 'notification.error-cantProcessRequest',
        message: 'error.internalError'
    },
    [ErrorNumber.EntityNotFound]: {
        // Error 7
        title: 'notification.error-cantProcessRequest',
        message: 'entityCouldNotBeFound'
    },
    [ErrorNumber.ParameterIsInvalid]: {
        // Error 8
        title: 'notification.error-cantProcessRequest',
        message: 'parameterIsNotValid'
    },
    [ErrorNumber.EntityFound]: {
        // Error 9
        title: 'notification.error-cantProcessRequest',
        message: 'entityWasFound'
    },
    [ErrorNumber.ParameterMissing]: {
        // Error 10
        title: 'notification.error-cantProcessRequest',
        message: 'error.parameterMissing'
    },
    [ErrorNumber.ParameterContainsInvalidId]: {
        // Error 17
        title: 'notification.error-cantProcessRequest',
        message: 'error.parameterContainsInvalidId'
    },
    [ErrorNumber.AccessDenied]: {
        // Error 23
        title: 'notification.error-cantProcessRequest',
        message: 'error.accessDenied'
    },
    [ErrorNumber.EntityAlreadyExists]: {
        // Error 25
        title: 'notification.error-cantProcessRequest',
        message: 'error.entityAlreadyExists'
    },
    [ErrorNumber.DownForMaintenance]: {
        // Error 27
        title: 'notification.error-cantProcessRequest',
        message: 'error.downForMaintenance'
    },
    [ErrorNumber.ParameterValueExists]: {
        // Error 44
        title: 'notification.error-cantProcessRequest',
        message: 'error.parameterValueExists'
    },
    [ErrorNumber.ConflictFound]: {
        // Error 47
        title: 'notification.error-cantProcessRequest',
        message: 'ConflictingStateFound'
    },
    [ErrorNumber.MyGroupDoesNotExist]: {
        // Error 48
        title: 'notification.error-cantProcessRequest',
        message: 'error.myGroupDoesNotExist'
    },
    [ErrorNumber.UserUsernameExists]: {
        // Error 51
        title: 'notification.error-genericTitle',
        message: 'AdminEditUserUsernameExists'
    },
    [ErrorNumber.InvalidUsername]: {
        // Error 52
        title: 'notification.error-genericTitle',
        message: 'UserNameMustBeAValidEmail'
    },
    [ErrorNumber.PasswordProblem]: {
        // Error 54
        title: 'notification.error-genericTitle',
        message: 'password.SomethingWrong'
    },
    [ErrorNumber.PasswordStrengthProblem]: {
        // Error 60
        title: 'password.passwordNotStrongEnoughTitle',
        message: 'password.passwordNotStrongEnough'
    }
};

const coreErrorToString: {
    [key: number]: {
        title: string;
        message: string;
    };
} = {
    // taken from Enums.ts
    [CoreErrorNumber.NotAuthorized]: {
        // Error 53
        title: 'notification.error-cantProcessRequest',
        message: 'error.notAuthorized'
    },
    [CoreErrorNumber.TooManyAPIRequests]: {
        // Error 54
        title: 'notification.error-cantProcessRequest',
        message: 'error.tooManyAPIRequests'
    }
};

export function notificationOnError(
    notificationErrorTitle?: string,
    notificationErrorMessage?: string
) {
    return notification(
        null,
        null,
        notificationErrorTitle,
        notificationErrorMessage
    );
}

/**
 * A notifications service that sends out notifications to NotificationComponents.
 * It only notifies the [[[[ most recently rendered NotificationComponent ]]]]
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
    piVersion: PIVersion;
    constructor(
        private toastr: ToastrService,
        private messageService: MessagingService,
        private TR: TranslationService,
        private tapestryNoficiationService: TapestryNotificationService,
        private init: InitDataService
    ) {
        this.piVersion = this.init.account.piVersion;

        this.messageService
            .listenFor<ErrorNotificationMessage>(ErrorNotificationMessage)
            .subscribe((msg) => {
                this.pushError(TR.key(msg.headerKey), TR.key(msg.bodyKey));
            });
    }

    private toastrOptions: Partial<IndividualConfig> = {
        closeButton: true,
        progressBar: true,
        positionClass: 'toast-bottom-right',
        timeOut: autoDismissNotificationTime
    };

    private toastrOptionsNoTimeout: Partial<IndividualConfig> = {
        closeButton: true,
        positionClass: 'toast-bottom-right',
        disableTimeOut: true
    };

    pushSuccess(title: string, message: string) {
        // Explicitly call out PI2 in case version is not returning a value, don't want to mess up Legacy notifications, can always fallback
        if (this.piVersion === PIVersion.Two) {
            const successNotification: TapestryNotification = {
                message: title,
                type: 'success',
                duration: autoDismissNotificationTime
            };

            // There should only ever be one success message coming out of this service, so no neeed to stack
            this.tapestryNoficiationService.create(successNotification, false);
        } else {
            return this.toastr.success(message, title, this.toastrOptions);
        }
    }

    pushError(title: string, message: string) {
        // Explicitly call out PI2 in case version is not returning a value, don't want to mess up Legacy notifications, can always fallback
        if (this.piVersion === PIVersion.Two) {
            const errorNotification: TapestryNotification = {
                // Unlike the success call, feels like the error should pass the relevant information
                message: message,
                type: 'error'
            };

            // Swallow the stacking and only display the last message.
            // In PI2 we typically do not see mixed messages coming from this service but rather state driving the same error over and over
            // Let's just show the final one.
            this.tapestryNoficiationService.create(errorNotification, false);
        } else {
            return this.toastr.error(
                message,
                title,
                this.toastrOptionsNoTimeout
            );
        }
    }
}
